/**
 * DeveloperRegistrationPage - Two-column marketing landing + registration form.
 * Serves both developer.ecgrid.com (!isDemo) and demo.ecgrid.com (isDemo).
 */

import { AnonymousPortalLayout } from '@/components/common/AnonymousPortalLayout';
import { ECGridColors } from '@/types/colors';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { useDeveloperRegistrationForm, type DeveloperRegistrationFormData } from './DeveloperRegistrationForm.hook';
import { registrationSchema, type RegistrationFormValues } from './developer-registration.schema';
import { PROGRAM_TYPE, type ProgramType } from '@/application/queries/developerRegistration';
import { RegistrationProgramPanel } from './RegistrationProgramPanel';
import { RegistrationFormSection } from './RegistrationFormSection';

// ── Props ───────────────────────────────────────────────────────────────────

export interface DeveloperRegistrationPageProps {
  antiforgeryToken?: string;
  programType?: ProgramType;
}

// ── Component ────────────────────────────────────────────────────────────────

export const DeveloperRegistrationPage = React.forwardRef<
  HTMLDivElement,
  DeveloperRegistrationPageProps
>(({ antiforgeryToken = '', programType = PROGRAM_TYPE.DEVELOPER }, ref) => {
  const { t } = useTranslation(NS.DEVELOPER_REGISTRATION);
  const isDemo = programType === PROGRAM_TYPE.DEMO;
  const formRef = useRef<HTMLDivElement>(null);
  const [formVisible, setFormVisible] = useState(false);

  // ── Form logic (unchanged) ───────────────────────────────────────────────
  const {
    accessCodeValidating, accessCodeResult, accessCodeError,
    isSubmitting, submitError, fieldErrors,
    validateAccessCode, submitForm, setSubmitError,
  } = useDeveloperRegistrationForm({ antiforgeryToken, programType });

  const { register, handleSubmit, formState: { errors }, watch } = useForm<RegistrationFormValues>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      companyName: '', companyAddress: '',
      ownerContactName: '', ownerContactEmail: '',
      technicalContactName: '', technicalContactEmail: '',
      accessCode: '',
    },
  });

  const accessCodeValue = watch('accessCode');

  const handleAccessCodeBlur = useCallback(() => {
    if (accessCodeValue) validateAccessCode(accessCodeValue);
  }, [accessCodeValue, validateAccessCode]);

  const showForm = useCallback(() => {
    setFormVisible(true);
    // Scroll after React renders the form
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const onSubmit = useCallback(
    (data: RegistrationFormValues) => {
      const formData: DeveloperRegistrationFormData = {
        companyName: data.companyName,
        companyAddress: data.companyAddress,
        ownerContactName: data.ownerContactName,
        ownerContactEmail: data.ownerContactEmail,
        technicalContactName: data.technicalContactName || undefined,
        technicalContactEmail: data.technicalContactEmail || undefined,
        accessCode: data.accessCode || undefined,
      };
      submitForm(formData);
    },
    [submitForm]
  );

  // ── Derived content ──────────────────────────────────────────────────────
  const heroTitle = isDemo
    ? t('DemoRegistration_Hero_Title', 'Not yet ready to commit? Try it out with our compliments.')
    : t('DeveloperRegistration_Hero_Title', 'You build the experience. We power the connections.');

  const heroSubtitle = isDemo
    ? t('DemoRegistration_Hero_Subtitle', 'Free access, no time limit, no credit card. Explore ECGrid\'s network and API surface at your own pace.')
    : t('DeveloperRegistration_Hero_Subtitle', 'Real infrastructure. Real APIs. A working development environment for engineering teams that build for production.');

  const heroParagraphs = isDemo
    ? [
        t('DemoRegistration_Hero_Para1'),
        t('DemoRegistration_Hero_Para2'),
      ]
    : [
        t('DeveloperRegistration_Hero_Para1'),
        t('DeveloperRegistration_Hero_Para2'),
        t('DeveloperRegistration_Hero_Para3'),
      ];

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <AnonymousPortalLayout ref={ref} mainClassName="flex-1 py-8 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto w-full">
        {/* Hero */}
        <div className="text-center mb-8 max-w-4xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: ECGridColors.tealDark }}>
            {heroTitle}
          </h1>
          <p className="text-sm text-gray-400 mb-4 leading-snug">
            {heroSubtitle}
          </p>
          <div className="space-y-2 text-sm text-gray-600 leading-relaxed text-left">
            {heroParagraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>

        {/* Two-column program panels */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <RegistrationProgramPanel
            variant={isDemo ? 'van-demo' : 'van-developer'}
            onCtaClick={showForm}
            isCtaDisabled={isSubmitting}
          />
          <RegistrationProgramPanel
            variant={isDemo ? 'simplify-demo' : 'simplify-developer'}
          />
        </div>

        {/* Registration form — shown on CTA click */}
        {formVisible && (
          <div ref={formRef} id="registration-form" className="max-w-2xl mx-auto">
            <RegistrationFormSection
              register={register}
              errors={errors}
              fieldErrors={fieldErrors}
              isSubmitting={isSubmitting}
              submitError={submitError}
              onFormSubmit={handleSubmit(onSubmit)}
              accessCodeValidating={accessCodeValidating}
              accessCodeResult={accessCodeResult}
              accessCodeError={accessCodeError}
              accessCodeValue={accessCodeValue}
              handleAccessCodeBlur={handleAccessCodeBlur}
              onDismissError={() => setSubmitError(null)}
              programType={programType}
            />
          </div>
        )}
      </div>
    </AnonymousPortalLayout>
  );
});

DeveloperRegistrationPage.displayName = 'DeveloperRegistrationPage';
